import {
  listTags,
  createTalentTag,
  updateTalentTag,
  deleteTalentTag,
} from "@fp/admin-api-client/client";
import type { TalentTagListItemDto } from "@fp/admin-api-client";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { DestructiveConfirmationModal } from "@fp/ui/components/application/modals/destructive-confirmation-modal";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { Button } from "@fp/ui/components/base/buttons/button";
import { Badge } from "@fp/ui/components/base/badges/badges";
import {
  createFileRoute,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";
import { Plus, Edit03, Trash01, SearchLg, Users01 } from "@untitledui/icons";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { resolveTagColor } from "@fp/ui/utils/tag-colors";
import { TagFormModal } from "./-components/tag-form-modal";
import { TagAssignmentsModal } from "./-components/tag-assignments-modal";

export type TagsSearchParams = {
  search?: string;
};

export const Route = createFileRoute("/settings/tags/")({
  validateSearch: (search: Record<string, unknown>): TagsSearchParams => {
    const value = search.search;
    return typeof value === "string" && value.length > 0
      ? { search: value }
      : {};
  },
  loaderDeps: ({ search }) => ({ search: search.search }),
  loader: async ({ deps }) => {
    const result = await listTags({
      page: 1,
      pageSize: 100,
      search: deps.search,
    });
    return { tags: result.data.items ?? [] };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { tags } = Route.useLoaderData();
  const { search: activeSearch } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const router = useRouter();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formModalTag, setFormModalTag] = useState<
    TalentTagListItemDto | null | undefined
  >(undefined);
  const [deleteModalTag, setDeleteModalTag] =
    useState<TalentTagListItemDto | null>(null);
  const [assignModalTag, setAssignModalTag] =
    useState<TalentTagListItemDto | null>(null);
  const [searchInput, setSearchInput] = useState(activeSearch ?? "");

  const isFormModalOpen = formModalTag !== undefined;
  const hasActiveSearch = Boolean(activeSearch);

  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === (activeSearch ?? "")) return;
    const timeout = setTimeout(() => {
      navigate({
        search: trimmed ? { search: trimmed } : {},
        replace: true,
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput, activeSearch, navigate]);

  const handleCreate = useCallback(
    async (data: {
      name: string;
      description?: string;
      hexColor?: string;
      isActive: boolean;
    }) => {
      setIsSubmitting(true);
      try {
        await createTalentTag(data);
        toast.success("Tag created");
        setFormModalTag(undefined);
        router.invalidate();
      } catch (error) {
        console.error("Failed to create tag", error);
        const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "Failed to create tag";
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [router],
  );

  const handleUpdate = useCallback(
    async (data: {
      name: string;
      description?: string;
      hexColor?: string;
      isActive: boolean;
    }) => {
      if (!formModalTag) return;
      setIsSubmitting(true);
      try {
        await updateTalentTag(formModalTag.id, data);
        toast.success("Tag updated");
        setFormModalTag(undefined);
        router.invalidate();
      } catch (error) {
        console.error("Failed to update tag", error);
        const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? "Failed to update tag";
        toast.error(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [formModalTag, router],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteModalTag) return;
    setIsSubmitting(true);
    try {
      await deleteTalentTag(deleteModalTag.id);
      toast.success(`"${deleteModalTag.name}" deleted`);
      setDeleteModalTag(null);
      router.invalidate();
    } catch (error) {
      console.error("Failed to delete tag", error);
      toast.error("Failed to delete tag");
    } finally {
      setIsSubmitting(false);
    }
  }, [deleteModalTag, router]);

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Tags"
        parent={{ url: "/settings", label: "Settings" }}
        titleActions={
          <Button color="primary" size="sm" onClick={() => setFormModalTag(null)}>
            <span className="flex items-center gap-1">
              <Plus className="size-4" />
              New Tag
            </span>
          </Button>
        }
      />
      <PageContentContainer className="space-y-0 bg-white p-0">
        {(tags.length > 0 || hasActiveSearch || searchInput.length > 0) && (
          <div className="px-4 py-3 border-b border-gray-200">
            <div className="relative">
              <SearchLg className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search tags..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>
          </div>
        )}
        {tags.length === 0 ? (
          <div className="px-4 py-12 text-center text-gray-500">
            {hasActiveSearch
              ? "No tags match your search."
              : "No tags yet. Create one to get started."}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {tags.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center justify-between px-4 py-4 first:border-t first:border-gray-200 last:border-b last:border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span
                      className="size-3 rounded-full flex-shrink-0 ring-1 ring-inset ring-black/10"
                      style={{ backgroundColor: resolveTagColor(tag.hexColor) }}
                    />
                    <span className="text-base font-medium text-gray-900">
                      {tag.name}
                    </span>
                    {!tag.isActive && (
                      <Badge size="sm" color="gray">
                        Inactive
                      </Badge>
                    )}
                    {tag.assignedTalentMemberCount > 0 && (
                      <span className="text-xs text-gray-500">
                        {tag.assignedTalentMemberCount}{" "}
                        {tag.assignedTalentMemberCount === 1 ? "talent member" : "talent members"}
                      </span>
                    )}
                  </div>
                  {tag.description && (
                    <p className="text-sm text-gray-500 mt-0.5 truncate">
                      {tag.description}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 ml-4">
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={() => setAssignModalTag(tag)}
                  >
                    <Users01 className="size-4" />
                  </Button>
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={() => setFormModalTag(tag)}
                  >
                    <Edit03 className="size-4" />
                  </Button>
                  <Button
                    color="tertiary"
                    size="sm"
                    onClick={() => setDeleteModalTag(tag)}
                  >
                    <Trash01 className="size-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </PageContentContainer>
      <TagFormModal
        isOpen={isFormModalOpen}
        onClose={() => setFormModalTag(undefined)}
        onSubmit={formModalTag ? handleUpdate : handleCreate}
        isLoading={isSubmitting}
        tag={formModalTag}
      />
      <DestructiveConfirmationModal
        isOpen={!!deleteModalTag}
        onClose={() => setDeleteModalTag(null)}
        onConfirm={handleDelete}
        title="Delete Tag"
        entity={{
          label: deleteModalTag?.name ?? "",
          swatchColor: resolveTagColor(deleteModalTag?.hexColor),
          description: deleteModalTag?.description ?? undefined,
        }}
        impactText={
          deleteModalTag?.assignedTalentMemberCount
            ? `This tag is currently assigned to ${deleteModalTag.assignedTalentMemberCount} ${deleteModalTag.assignedTalentMemberCount === 1 ? "talent member" : "talent members"}.`
            : undefined
        }
        confirmText="Delete Tag"
        isLoading={isSubmitting}
      />
      {assignModalTag && (
        <TagAssignmentsModal
          isOpen={!!assignModalTag}
          onClose={() => setAssignModalTag(null)}
          onChanged={() => router.invalidate()}
          tag={assignModalTag}
        />
      )}
    </AdminLayout>
  );
}
